// src/components/layout/ChatColumn/ChatColumn.jsx
import React, { useEffect, useRef, useCallback, useMemo, useState, useLayoutEffect } from 'react';
import { authManager } from '@/managers/AuthManager';
import { useConversation } from '@/managers/ConvoManager';
import { useChatFeed } from '@/managers/ChatFeedManager/ChatFeedManager';
import { usePanelManager, PanelTypes, CONTENT_TYPES } from '@/managers/PanelManager';
import UserBubble from './UserBubble';
import BotBubble from './BotBubble';
import SystemMessage from './SystemMessage';
import ChatContextDivider from './ChatContextDivider';
import MessageSkeleton from './MessageSkeleton';
import ChatAreaLoader from './ChatAreaLoader';
import LoadingDots from './LoadingDots';
import SummaryMessage from './SummaryMessage';

const MOBILE_BREAKPOINT = 768;

// ARCHITECTURAL FIX: Define a stable, empty function to prevent re-renders.
const noOp = () => {};

const MemoizedSystemMessage = React.memo(SystemMessage, (prevProps, nextProps) => {
  return (
    prevProps.message === nextProps.message &&
    prevProps.timestamp === nextProps.timestamp &&
    prevProps.hasError === nextProps.hasError &&
    prevProps.isDecrypting === nextProps.isDecrypting
  );
});

const getThoughtPanelInfo = (thoughtSummary, retrievedMemories, memoryStatus, personalMemoryEnabled) => {
  if (!thoughtSummary && (!retrievedMemories || retrievedMemories.length === 0)) {
    return { hasThoughts: false, hasSurfacedMemories: false, shouldDefaultToCollapsed: false };
  }

  const memoriesAreLoading = memoryStatus === 'loading';
  const memoriesHadError = memoryStatus === 'error';

  const hasSurfacedMemories =
    personalMemoryEnabled &&
    !memoriesAreLoading &&
    !memoriesHadError &&
    retrievedMemories &&
    retrievedMemories.length > 0;

  const hasThoughts = thoughtSummary && thoughtSummary.trim().length > 0;

  return {
    hasThoughts,
    hasSurfacedMemories,
    shouldDefaultToCollapsed: hasSurfacedMemories && !hasThoughts,
  };
};

const ChatColumn = ({ className = '' }) => {
  const containerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  const [bottomPadding, setBottomPadding] = useState(24);

  const {
    loadMoreMessagesIfNeeded,
    currentConversationId: activeConversationId,
    currentConversation,
    rewindToMessage,
    deleteMessage,
    regenerateSummary,
    isConversationLoading,
    isNavigating,
    updateConversation
  } = useConversation();

  const { cancelStream, fixMessageFormatting, redactMessageContent } = useChatFeed();
  const { isPanelOpen, showInPortal } = usePanelManager();
  const [authState, setAuthState] = useState(() => authManager.getState());

  useEffect(() => {
    const unsubscribe = authManager.subscribe(setAuthState);
    return unsubscribe;
  }, []);

  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [thoughtPanelStates, setThoughtPanelStates] = useState({});
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { settings, tier } = authState;
  const personalMemoryEnabled = settings.zPreferences?.userMeta?.usePersonalMemory ?? false;
  const chatFontSize = settings.zPreferences?.chatFontSize || 'md';

  const containerStyle = useMemo(() => {
    const inputHeight = settings.zPreferences?.inputHeight || 80;
    return { paddingBottom: `${inputHeight + 100}px` };
  }, [settings.zPreferences?.inputHeight]);

  const isConvoInflight = useMemo(() => {
    return currentConversation?.status === 'inflight' || currentConversation?.status === 'streaming';
  }, [currentConversation?.status]);

  const allMessages = useMemo(() => {
    return currentConversation?.messages?.filter(msg => msg && !msg.hidden && msg.id && !msg._isPlaceholder) || [];
  }, [currentConversation?.messages]);

  const allMessagesRef = useRef(allMessages);
  allMessagesRef.current = allMessages;

  const actualMemorySize = currentConversation?.memorySize ?? (tier === 'Free' ? 10 : 20);

  const isPanelOpenForLayout = useMemo(() => {
    if (isMobileView) return false;
    return isPanelOpen(PanelTypes.PORTAL) || isPanelOpen(PanelTypes.CODER) || isPanelOpen(PanelTypes.SNIPPETS) || isPanelOpen(PanelTypes.REACT_MOCKUP);
  }, [isPanelOpen, isMobileView]);

  const isCurrentConvoInitiallyLoading = useMemo(() => {
    if (!currentConversation?.id) return false;
    return isConversationLoading(currentConversation.id);
  }, [currentConversation?.id, isConversationLoading]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentMessageCount = allMessages.length;
    const isNewMessage = currentMessageCount > lastMessageCountRef.current;

    if (lastMessageCountRef.current === 0 && currentMessageCount > 0) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
      lastMessageCountRef.current = currentMessageCount;
      return;
    }

    if (isNewMessage) {
      const lastMsg = allMessages[allMessages.length - 1];

      if (lastMsg && lastMsg.role === 'assistant') {
        const viewportHeight = window.innerHeight;
        const dynamicTapeHeight = viewportHeight * 0.50;
        setBottomPadding(dynamicTapeHeight);

        requestAnimationFrame(() => {
          const currentContainer = containerRef.current;
          if (!currentContainer) return;
          const lastMsgId = allMessages[allMessages.length - 1]?.id;
          if (!lastMsgId) return;
          const lastMessageElement = currentContainer.querySelector(`[data-message-id="${lastMsgId}"]`);
          if (lastMessageElement) {
            const targetScrollTop = lastMessageElement.offsetTop - (viewportHeight * 0.20);
            currentContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
          }
        });
      }
    }

    lastMessageCountRef.current = currentMessageCount;
  }, [allMessages]);

  const handleCodeBlockClick = useCallback((code, language, messageId, blockIndex) => {
    if (isMobileView) return;
    showInPortal({
      type: CONTENT_TYPES.CODE,
      content: code,
      metadata: {
        language: language || 'plaintext',
        messageId: messageId,
        blockIndex: blockIndex,
        title: `${language || 'Code'} Block from message ${messageId ? messageId.slice(-6) : 'N/A'}`
      }
    });
  }, [showInPortal, isMobileView]);

  const handleToggleThoughtPanel = useCallback((messageId) => {
    setThoughtPanelStates(prevStates => {
      const message = allMessagesRef.current.find(m => m.id === messageId);
      if (!message) return prevStates;
      
      const { shouldDefaultToCollapsed } = getThoughtPanelInfo(
        message.thoughtSummary || '',
        message.retrievedMemories || [],
        message.memoryStatus,
        personalMemoryEnabled
      );
      const currentCollapsedState = prevStates[messageId] ?? shouldDefaultToCollapsed;
      return { ...prevStates, [messageId]: !currentCollapsedState };
    });
  }, [personalMemoryEnabled]);

  const handleRewindFromBotError = useCallback(async (botMessageId) => {
    const currentMessages = allMessagesRef.current;
    const botMessageIndex = currentMessages.findIndex(m => m.id === botMessageId);
    if (botMessageIndex < 0) {
      console.error(`[ChatColumn] Could not find bot message ${botMessageId} to rewind from.`);
      return;
    }

    let userMessageToRewind = null;
    for (let i = botMessageIndex - 1; i >= 0; i--) {
      if (currentMessages[i].role === 'user') {
        userMessageToRewind = currentMessages[i];
        break;
      }
    }

    if (userMessageToRewind) {
      await rewindToMessage(userMessageToRewind.id);
    } else {
      console.warn(`[ChatColumn] Could not find a preceding user message to rewind to. Deleting only the error message: ${botMessageId}`);
      await deleteMessage(botMessageId);
    }
  }, [rewindToMessage, deleteMessage]);

  const handleRegenerateSummary = useCallback((messageIdToRegenerate) => {
    if (activeConversationId && regenerateSummary) {
      regenerateSummary(activeConversationId, messageIdToRegenerate);
    }
  }, [activeConversationId, regenerateSummary]);

  useEffect(() => {
    if (!currentConversation?.hasMoreMessages || !topSentinelRef.current || !containerRef.current || isCurrentConvoInitiallyLoading || isNavigating) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && currentConversation?.hasMoreMessages && !isLoadingOlder) {
          if (activeConversationId === currentConversation?.id) {
            setIsLoadingOlder(true);
            const currentContainer = containerRef.current;
            if (!currentContainer) {
              setIsLoadingOlder(false);
              return;
            }
            const oldScrollHeight = currentContainer.scrollHeight;
            const oldScrollTop = currentContainer.scrollTop;
            loadMoreMessagesIfNeeded(activeConversationId, allMessages.length + 25)
              .then(() => {
                requestAnimationFrame(() => {
                  if (containerRef.current) {
                    const newScrollHeight = containerRef.current.scrollHeight;
                    const heightDiff = newScrollHeight - oldScrollHeight;
                    containerRef.current.scrollTop = oldScrollTop + heightDiff;
                  }
                });
              })
              .finally(() => setIsLoadingOlder(false));
          }
        }
      },
      { root: containerRef.current, threshold: 0.1, rootMargin: '50px 0px 0px 0px' }
    );
    if (topSentinelRef.current) observer.observe(topSentinelRef.current);
    return () => { observer.disconnect(); };
  }, [currentConversation?.id, currentConversation?.hasMoreMessages, isLoadingOlder, loadMoreMessagesIfNeeded, activeConversationId, isCurrentConvoInitiallyLoading, isNavigating, allMessages.length]);

  const getMessageOpacity = useCallback((indexInAll) => {
    const memSize = currentConversation?.memorySize ?? 20;
    if (allMessages.length <= memSize) return 1;
    const messagesFromEnd = allMessages.length - indexInAll - 1;
    return messagesFromEnd >= memSize ? 0.4 : 1;
  }, [currentConversation?.memorySize, allMessages.length]);

  return (
    <div
      ref={containerRef}
      className={`relative flex-1 overflow-y-auto hide-scrollbar ${className}`}
      style={containerStyle}
      role="log"
      aria-live="polite"
    >
      {isCurrentConvoInitiallyLoading && <ChatAreaLoader message="Loading conversation..." />}
      <div
        className="w-full max-w-3xl mx-auto px-4"
        style={{ paddingBottom: `${bottomPadding}px` }}
      >
        {currentConversation?.hasMoreMessages && !isLoadingOlder && (
          <div ref={topSentinelRef} className="h-10 -mt-10" aria-hidden="true" />
        )}
        {isLoadingOlder && (
          <div className="flex justify-center py-2">
            <div className="w-1 h-1 bg-on-surface-dim rounded-full animate-pulse opacity-50" />
          </div>
        )}
        {!isCurrentConvoInitiallyLoading && allMessages.map((message, index) => {
          if (!message || !message.id) return null;
          const isContextBoundary = index === allMessages.length - (currentConversation?.memorySize ?? 20) - 1;
          const isLastMessage = index === allMessages.length - 1;
          if (message._pendingDecryption) {
            return <MessageSkeleton key={message.id} role={message.role} />;
          }
          const isLoadingOrStreaming = isConvoInflight && isLastMessage && message.role === 'assistant';
          const firstMessageIndexInWindow = allMessages.findIndex(msg => msg.role !== 'system');
          const isFirstActualMessageInWindow = index === firstMessageIndexInWindow;
          
          const thoughtSummary = message.thoughtSummary || '';
          const retrievedMemories = message.retrievedMemories || [];
          const memoryStatus = message.memoryStatus || 'idle';
          
          const contentBlocks = message.contentBlocks || [];
          const hasThinkingBlock = contentBlocks.some(b => b.type === 'thinking');
          const isThinkingEnabled = message.metadata?.allowThinking ?? false;
          
          const { shouldDefaultToCollapsed } = getThoughtPanelInfo(
            thoughtSummary,
            retrievedMemories,
            memoryStatus,
            personalMemoryEnabled
          );
          const isThoughtPanelCollapsed = thoughtPanelStates[message.id] ?? shouldDefaultToCollapsed;
          
          return (
            <React.Fragment key={message.id}>
              <div style={{ opacity: getMessageOpacity(index) }}>
                {message.type === 'summary' ? (
                  <SummaryMessage
                    status={message.status}
                    contentBlocks={contentBlocks}
                    error={message.errorDetails?.message}
                    platform={message.metadata?.platform}
                    message={message}
                    onRegenerate={() => handleRegenerateSummary(message.id)}
                    onDelete={() => deleteMessage(message.id)}
                  />
                ) : message.type === 'context_summary' ? (
                  <div className="w-full flex items-end mb-4 justify-start">
                    <div className={'max-w-[90%]'}>
                      <BotBubble
                        messageId={message.id}
                        timestamp={message.timestamp}
                        fullMessage={message}
                        isLoadingOrStreaming={false}
                        conversationStatus="idle"
                        onRewindToMessage={handleRewindFromBotError}
                        cancelStream={noOp}
                        onFixFormatting={fixMessageFormatting}
                        onRedactContent={redactMessageContent}
                        chatFontSize={String(chatFontSize)}
                        personalMemoryEnabled={personalMemoryEnabled}
                        isThoughtPanelCollapsed={true}
                        onToggleThoughtPanel={noOp}
                        thoughtSummary=""
                        isThinkingEnabled={false}
                        contentBlocks={contentBlocks}
                        retrievedMemories={[]}
                        memoryStatus="idle"
                        className="context-summary-message"
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className={`w-full flex items-end mb-4 ${isFirstActualMessageInWindow && message.role === 'user' ? 'mt-9' : 'mt-2'} ${message.role === 'system' && message.type === 'bot-switch' ? 'justify-center' : message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    data-message-id={message.id}
                  >
                    <div className={`${message.role === 'system' && message.type === 'bot-switch' ? 'w-full' : message.role === 'user' ? 'max-w-[83%]' : isPanelOpenForLayout ? 'max-w-[85%]' : 'max-w-[90%]'}`}>
                      {message.role === 'user' ? (
                        <UserBubble
                          messageId={message.id}
                          contentBlocks={contentBlocks}
                          fullMessageObject={message}
                          timestamp={message.timestamp}
                          onCodeBlockClick={handleCodeBlockClick}
                        />
                      ) : message.role === 'system' ? (
                        <MemoizedSystemMessage
                          message={message.rawContent}
                          timestamp={message.timestamp}
                          hasError={!!message.errorDetails || message.status?.startsWith('error')}
                          variant={message.errorDetails ? 'error' : 'default'}
                          fontSize={chatFontSize}
                          type={message.type || 'INFO'}
                          metadata={message.metadata || {}}
                          isDecrypting={message._pendingDecryption === true}
                        />
                      ) : (
                        <BotBubble
                          messageId={message.id}
                          timestamp={message.timestamp}
                          fullMessage={message}
                          isLoadingOrStreaming={isLoadingOrStreaming}
                          conversationStatus={currentConversation?.status}
                          onRewindToMessage={handleRewindFromBotError}
                          cancelStream={cancelStream}
                          onFixFormatting={fixMessageFormatting}
                          onRedactContent={redactMessageContent}
                          chatFontSize={String(chatFontSize)}
                          personalMemoryEnabled={personalMemoryEnabled}
                          isThoughtPanelCollapsed={isThoughtPanelCollapsed}
                          onToggleThoughtPanel={handleToggleThoughtPanel}
                          thoughtSummary={thoughtSummary}
                          isThinkingEnabled={isThinkingEnabled}
                          contentBlocks={contentBlocks}
                          retrievedMemories={retrievedMemories}
                          memoryStatus={memoryStatus}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isContextBoundary && (<ChatContextDivider messageCount={actualMemorySize} />)}
            </React.Fragment>
          );
        })}
        {isConvoInflight && allMessages.length > 0 && allMessages[allMessages.length - 1].role === 'user' && (
          <div className="w-full flex items-end mb-4 mt-2 justify-start" data-message-id="inflight-placeholder">
            <div className={isPanelOpenForLayout ? 'max-w-[85%]' : 'max-w-[90%]'}>
              <BotBubble
                messageId={`inflight-placeholder-${activeConversationId}`}
                timestamp={new Date()}
                fullMessage={{ status: currentConversation?.status || 'inflight' }}
                isLoadingOrStreaming={true}
                conversationStatus={currentConversation?.status}
                cancelStream={cancelStream}
                chatFontSize={String(chatFontSize)}
                contentBlocks={[]}
                onRewindToMessage={noOp}
                onFixFormatting={fixMessageFormatting}
                onRedactContent={redactMessageContent}
                personalMemoryEnabled={personalMemoryEnabled}
                isThoughtPanelCollapsed={true}
                onToggleThoughtPanel={noOp}
                thoughtSummary=""
                isThinkingEnabled={false}
                retrievedMemories={[]}
                memoryStatus="idle"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatColumn);